// Site inventory (migration 20261103190000): every Netlify site and Cloudways app the
// hosting runner can see, synced into hosting_inventory about hourly. Hosting → Site
// inventory reads it next to the uptime monitor; how a row stands (attached, monitored
// with no account, not monitored, ignored) and which account it likely belongs to come
// from siteInventoryRules.mjs, shared with the runner's dry run.
//
// Assigning is ordinary hosting wiring: a monitored_sites row carrying the provider id
// and hosting_account_id, created or reused. Ignoring is the only write to this table.

import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { HostedSite } from './hosting';
import { bareHost, matchSites, siteKindFor, urlFor } from './siteInventoryRules.mjs';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type LooseClient = SupabaseClient<any, 'public', any>;
const db = supabase as unknown as LooseClient;

export type InventoryRow = {
  id: string;
  provider: 'netlify' | 'cloudways';
  external_id: string;
  name: string;
  primary_domain: string | null;
  aliases: string[];
  repo: string | null;
  server_id: string | null;
  server_label: string | null;
  app_id: string | null;
  last_published_at: string | null;
  serves_here: boolean | null;
  seen_at: string;
  gone_at: string | null;
  decision: 'ignored' | null;
  decision_note: string | null;
  decided_by: string | null;
  decided_by_name: string | null;
  decided_at: string | null;
};

/** The reasons offered when ignoring a site; the chip label is stored as decision_note. */
export const IGNORE_REASONS = ['Not ours', 'Dev or preview', 'Retired', 'Other'] as const;

/** null: the table isn't there yet (migration not applied), so the screen can say so instead of failing. */
export async function fetchInventory(): Promise<InventoryRow[] | null> {
  const { data, error } = await db.from('hosting_inventory').select('*').order('primary_domain', { nullsFirst: false }).order('name');
  if (error?.code === '42P01' || error?.code === 'PGRST205') return null;
  if (error) throw error;
  return (data || []) as InventoryRow[];
}

export async function setInventoryDecision(id: string, decision: 'ignored' | null, note: string | null, by: { id: string; name: string } | null): Promise<void> {
  const row = decision
    ? { decision, decision_note: note, decided_by: by?.id ?? null, decided_by_name: by?.name ?? null, decided_at: new Date().toISOString() }
    : { decision: null, decision_note: null, decided_by: null, decided_by_name: null, decided_at: null };
  const { data, error } = await db.from('hosting_inventory').update(row).eq('id', id).select('id');
  if (error) throw error;
  if (!data?.length) throw new Error('The site was not updated');
}

const providerIds = (row: InventoryRow) =>
  row.provider === 'netlify' ? { netlify_site_id: row.external_id } : { cloudways_server_id: row.server_id, cloudways_app_id: row.app_id };

/** What assigning changed, so Undo can put it back. */
export type AssignUndo =
  | { kind: 'created'; siteId: string }
  | { kind: 'updated'; siteId: string; before: Pick<HostedSite, 'hosting_account_id' | 'netlify_site_id' | 'cloudways_server_id' | 'cloudways_app_id'> };

/**
 * Put an inventory site on an account. Reuses the monitored site that is this row (linked by id, else same host),
 * filling its provider id when it has none; otherwise adds one to the uptime monitor. The provider id is only set
 * when the domain answers from that provider (or there is no domain), the rule the runner's link-* commands keep;
 * otherwise the site is attached unlinked and auto-link keeps looking.
 */
export async function assignInventorySite(row: InventoryRow, accountId: string, sites: HostedSite[]): Promise<AssignUndo> {
  const ids = row.serves_here === false ? {} : providerIds(row);
  const existing = matchSites(row, sites)[0]?.site;
  const at = new Date().toISOString();
  if (existing) {
    const fill = existing.netlify_site_id || existing.cloudways_app_id ? {} : ids;
    const { data, error } = await db
      .from('monitored_sites')
      .update({ hosting_account_id: accountId, ...fill, updated_at: at })
      .eq('id', existing.id)
      .select('id');
    if (error) throw error;
    if (!data?.length) throw new Error('The site was not updated');
    const { hosting_account_id, netlify_site_id, cloudways_server_id, cloudways_app_id } = existing;
    return { kind: 'updated', siteId: existing.id, before: { hosting_account_id, netlify_site_id, cloudways_server_id, cloudways_app_id } };
  }
  const url = urlFor(row);
  if (!url) throw new Error('This site has no domain to watch');
  const { data, error } = await db
    .from('monitored_sites')
    .insert({
      name: row.primary_domain ? bareHost(row.primary_domain) : row.name,
      url,
      is_active: true,
      alert_recipients: [],
      hosting_account_id: accountId,
      ...ids,
      // admin./app./… hosts are apps; anything else waits for the setup job to decide.
      site_kind: siteKindFor(url),
    })
    .select('id')
    .single();
  if (error) throw error;
  return { kind: 'created', siteId: data.id };
}

/** Put an assignment back: delete the monitored site it added (uptime checks go with it), or restore the one it changed. */
export async function undoAssign(undo: AssignUndo): Promise<void> {
  if (undo.kind === 'created') {
    const { error } = await db.from('monitored_sites').delete().eq('id', undo.siteId);
    if (error) throw error;
    return;
  }
  const { error } = await db
    .from('monitored_sites')
    .update({ ...undo.before, updated_at: new Date().toISOString() })
    .eq('id', undo.siteId);
  if (error) throw error;
}
